import './MarketplaceBuyerNdas.scss';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../../store/selectors';
import Helpers from '../../../../utils/helper';
import BootstrapTable from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import paginationFactory from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-paginator';
import { TableFilter } from '../../../../components/Table/models/table-filter';
import filterFactory from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-filter';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import { AddBuyerNdaTemplateModal } from './AddBuyerNdaTemplateModal/AddBuyerNdaTemplateModal';
import { BuyerNdaTemplate } from '../../../../slices/marketplace/timeline/models/buyer-nda-template.model';
import { GetBuyerNdaTemplates } from '../../../../slices/marketplace/timeline/thunks/get-buyer-nda-templates.thunk';
import createNotification from '../../../../utils/createNotification';

const MarketplaceBuyerNdas = () => {
    const dispatch = useDispatch() as any;
    const buyerNdaTemplatesTable = useStateSelector(
        (state) => state.timelines.buyerNdaTemplatesTable
    );
    const axios = useStateSelector((state) => state.core.axios);
    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(5);
    const [templates, setTemplates] = useState<BuyerNdaTemplate[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<BuyerNdaTemplate>();
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isRemoveConfirmationPopupVisible, setIsRemoveConfirmationPopupVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const defaultSorted: [{ dataField: any; order: any }] = [
        {
            dataField: 'id',
            order: 'desc',
        },
    ];

    const [currentSort, setCurrentSort] = useState<TableFilter>(
        new TableFilter({ filters: [], logic: '' }, page, sizePerPage, {
            dir: 'desc',
            field: 'Id',
        })
    );

    const columns = [
        {
            dataField: 'id',
            text: 'Id',
            hidden: true,
        },
        {
            dataField: 'name',
            text: 'Template Name',
            headerClasses: 'name-column-header',
            sort: true,
            formatter: (cellContent: string) =>
                !cellContent ? 'NA' : cellContent,
        },
        {
            dataField: '',
            text: 'Actions',
            headerClasses: 'actions-column-header',
            headerFormatter: () => (
                <div className="d-flex justify-content-between w-100">
                    <div className="w-100">Actions</div>
                </div>
            ),
            style: {
                minWidth: 100,
            },
            formatter: (_cellContent: number, row: BuyerNdaTemplate) => {
                return (
                    <div className="action-icons">
                        <FontAwesomeIcon
                            className={`delete-button cursor-pointer ${row.isInUse ? 'disabled-row' : ''}`}
                            icon={faTrashCan}
                            onClick={() => {
                                setSelectedTemplate(row);
                                setIsRemoveConfirmationPopupVisible(true);
                            }}
                        />
                    </div>
                );
            },
        },
    ];

    const handleTableChange = (
        _type: any,
        newState: {
            page: number;
            sizePerPage: number;
            sortField: string;
            sortOrder: 'asc' | 'desc';
            filters: {
                [key: string]: {
                    filterVal: any;
                    filterType: string;
                    comparator: any;
                };
            };
        }
    ) => {
        const updatedPostObj = new TableFilter(
            { filters: [], logic: '' },
            newState.page,
            newState.sizePerPage,
            { dir: newState.sortOrder, field: newState.sortField }
        );

        setCurrentSort(updatedPostObj);
        setPage(newState.page);
        setSizePerPage(newState.sizePerPage);

        fetchBuyerNdaTemplates(updatedPostObj);
    };

    const fetchBuyerNdaTemplates = async (newSort?: TableFilter) => {
        await dispatch(GetBuyerNdaTemplates(newSort || currentSort));
    };

    const handleRemoveTemplate = async (id: number) => {
        setIsLoading(true);
        const response = await axios.delete(
            `/api/Marketplace/MarketplaceBuyersNdaTemplates/${id}`
        );
        if (response.status !== 200 && response.status !== 204) {
            createNotification(
                'An unexpected error occurred during template removal.',
                'error'
            );
            setIsLoading(false);
            return;
        } else {
            await fetchBuyerNdaTemplates();
            setIsRemoveConfirmationPopupVisible(false);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setIsLoading(true);
        fetchBuyerNdaTemplates().finally(() => {
            setIsLoading(false);
        });
    }, []);

    useEffect(() => {
        setTemplates(buyerNdaTemplatesTable.templates);
        setPage(buyerNdaTemplatesTable.pageNumber);
        setTotalSize(buyerNdaTemplatesTable.totalItems);
        setSizePerPage(buyerNdaTemplatesTable.pageSize);
    }, [buyerNdaTemplatesTable]);

    return (
        <>
            <div className="marketplace-buyer-ndas">
                <div>
                    <div className="mb-3">
                        <strong>Marketplace Buyer NDAs</strong>
                        <Button
                            type="button"
                            className="btn btn-primary ml-3"
                            onClick={() => {
                                setIsAddModalVisible(true);
                            }}>
                            Add New
                        </Button>
                    </div>
                    <div
                        role="tabpanel"
                        id="panel-buyer-ndas"
                        aria-labelledby="tab-buyer-ndas"
                        aria-hidden="false">
                        <div className="card-body">
                            {isLoading ? (
                                Helpers.renderTableLoader()
                            ) : (
                                <BootstrapTable
                                    striped
                                    bordered
                                    bootstrap4
                                    remote
                                    keyField="id"
                                    data={templates}
                                    columns={columns}
                                    defaultSorted={defaultSorted}
                                    filter={filterFactory()}
                                    pagination={paginationFactory({
                                        page,
                                        sizePerPage,
                                        totalSize,
                                        tooltipTargetId:
                                            'buyer-nda-templates-tooltip',
                                    })}
                                    onTableChange={handleTableChange}
                                    noDataIndication="No templates to display"
                                    rowStyle={{ height: '44px' }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {isAddModalVisible ? (
                <AddBuyerNdaTemplateModal
                    onClose={() => setIsAddModalVisible(false)}
                    isOpened={isAddModalVisible}
                    afterSuccessfulAdd={() => {
                        fetchBuyerNdaTemplates();
                        setIsAddModalVisible(false);
                    }}
                />
            ) : (
                []
            )}

            {isRemoveConfirmationPopupVisible ? (
                <ConfirmationModal
                    isVisible={isRemoveConfirmationPopupVisible}
                    setIsVisible={() => {
                        setSelectedTemplate(undefined);
                        setIsRemoveConfirmationPopupVisible(false);
                    }}
                    header={'Are you sure?'}
                    confirmationText={
                        'Are you sure you want to delete this template?'
                    }
                    onConfirm={() => handleRemoveTemplate(selectedTemplate.id)}
                    params={undefined}
                    cancelButtonText={'No, take me back'}
                    nextButtonText={`Yes, I'm sure`}
                />
            ) : (
                []
            )}
        </>
    );
};

export default MarketplaceBuyerNdas;
